import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface TableInfo {
  name: string;
  columns: ColumnInfo[];
  rowCount: number;
  size: string;
}

interface ColumnInfo {
  name: string;
  type: string;
  nullable: boolean;
  defaultValue: string;
  primaryKey: boolean;
}

interface TableData {
  tableName: string;
  columns: string[];
  rows: Record<string, any>[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

const DatabaseViewer: React.FC = () => {
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  
  const navigate = useNavigate();

  // Check admin access
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user || user.role !== 'admin') {
      navigate('/');
      toast.error('You do not have permission to access the database viewer');
      return;
    }
    
    loadSchema();
  }, [navigate]);

  // Load table data when table is selected
  useEffect(() => {
    if (selectedTable) {
      loadTableData(selectedTable, currentPage);
    }
  }, [selectedTable, currentPage]);

  const loadSchema = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/database/schema', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load database schema: ${response.statusText}`);
      }
      
      const data = await response.json();
      setTables(data.tables || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load schema';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadTableData = async (tableName: string, page: number = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/database/table/${tableName}?page=${page}&limit=50`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load table data: ${response.statusText}`);
      }
      
      const data = await response.json();
      setTableData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load table data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatCellValue = (value: any) => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">NULL</span>;
    }
    
    if (typeof value === 'object' && value.type) {
      switch (value.type) {
        case 'json':
          return (
            <details className="cursor-pointer">
              <summary className="text-blue-600 hover:text-blue-800 truncate">
                JSON ({JSON.stringify(JSON.parse(value.value)).length} chars)
              </summary>
              <div className="mt-2 p-3 bg-gray-100 rounded overflow-auto max-h-64 text-xs font-mono">
                <pre className="whitespace-pre-wrap">
                  {JSON.stringify(JSON.parse(value.value), null, 2)}
                </pre>
              </div>
            </details>
          );
        case 'timestamp':
          return (
            <span title={`Unix: ${value.value}`} className="text-green-600 whitespace-nowrap">
              {value.formatted}
            </span>
          );
        default:
          return (
            <span className="truncate block" title={String(value.value)}>
              {String(value.value)}
            </span>
          );
      }
    }
    
    if (value === '[MASKED]') {
      return <span className="text-red-500 font-mono">••••••••</span>;
    }
    
    const stringValue = String(value);
    if (stringValue.length > 50) {
      return (
        <details className="cursor-pointer">
          <summary className="text-blue-600 hover:text-blue-800 truncate">
            {stringValue.substring(0, 50)}... ({stringValue.length} chars)
          </summary>
          <div className="mt-2 p-3 bg-gray-100 rounded overflow-auto max-h-32 text-xs">
            <div className="whitespace-pre-wrap break-all">
              {stringValue}
            </div>
          </div>
        </details>
      );
    }
    
    return (
      <span className="block truncate" title={stringValue}>
        {stringValue}
      </span>
    );
  };

  const filteredTables = tables.filter(table =>
    table.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleTableSelect = (tableName: string) => {
    setSelectedTable(tableName);
    setCurrentPage(1);
    setTableData(null);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin')}
                className="mr-4 text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Database Viewer</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">{tables.length} tables</span>
              <button
                onClick={loadSchema}
                disabled={loading}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex h-[calc(100vh-200px)] bg-white rounded-lg shadow">
          {/* Sidebar - Table List */}
          <div className="w-80 flex-shrink-0 border-r border-gray-200 overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Tables</h2>
              <input
                type="text"
                placeholder="Search tables..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            <div className="p-2">
              {loading && !selectedTable ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div>
                </div>
              ) : (
                filteredTables.map((table) => (
                  <div
                    key={table.name}
                    onClick={() => handleTableSelect(table.name)}
                    className={`p-3 rounded-lg cursor-pointer mb-2 transition-colors ${
                      selectedTable === table.name
                        ? 'bg-indigo-100 border border-indigo-300'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{table.name}</div>
                    <div className="text-sm text-gray-500">
                      {table.rowCount.toLocaleString()} rows • {table.columns.length} columns
                    </div>
                    <div className="text-xs text-gray-400">{table.size}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Main Content - Table Data */}
          <div className="flex-1 flex flex-col">
            {selectedTable ? (
              <>
                {/* Table Header */}
                <div className="border-b border-gray-200 p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h1 className="text-xl font-semibold text-gray-900">{selectedTable}</h1>
                      {tableData && (
                        <div className="mt-1">
                          <p className="text-sm text-gray-500">
                            {tableData.pagination.total === 0 ? (
                              "No rows found"
                            ) : (
                              `Showing ${((tableData.pagination.page - 1) * tableData.pagination.limit) + 1}-${Math.min(tableData.pagination.page * tableData.pagination.limit, tableData.pagination.total)} of ${tableData.pagination.total.toLocaleString()} rows`
                            )}
                          </p>
                          {tableData.pagination.total > 0 && (
                            <p className="text-xs text-blue-600 flex items-center mt-1">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                              </svg>
                              Sorted by newest first
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    {tableData && tableData.pagination.totalPages > 1 && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={!tableData.pagination.hasPrev}
                          className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-600">
                          Page {tableData.pagination.page} of {tableData.pagination.totalPages}
                        </span>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={!tableData.pagination.hasNext}
                          className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Table Content */}
                <div className="flex-1 overflow-auto">
                  {loading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
                    </div>
                  ) : error ? (
                    <div className="p-4">
                      <div className="text-red-600 p-4 bg-red-50 rounded-lg">
                        Error: {error}
                      </div>
                    </div>
                  ) : tableData ? (
                    tableData.rows.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full table-auto divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              {tableData.columns.map((column) => (
                                <th
                                  key={column}
                                  className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 last:border-r-0 whitespace-nowrap"
                                  style={{ minWidth: '150px' }}
                                >
                                  <div className="truncate" title={column}>
                                    {column}
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {tableData.rows.map((row, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                {tableData.columns.map((column) => (
                                  <td
                                    key={column}
                                    className="px-6 py-4 text-sm text-gray-900 border-r border-gray-200 last:border-r-0"
                                    style={{ minWidth: '150px', maxWidth: '300px' }}
                                  >
                                    <div className="break-words">
                                      {formatCellValue(row[column])}
                                    </div>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      // Empty state for tables with no data
                      <div className="flex-1 flex items-center justify-center">
                        <div className="text-center py-12">
                          <div className="text-gray-400 mb-4">
                            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-2M4 13h2m0 0V9a2 2 0 012-2h2m0 0V6a2 2 0 012-2h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 01.293.707V9M16 13v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
                          <p className="text-gray-500 mb-4">
                            The <span className="font-mono bg-gray-100 px-2 py-1 rounded">{selectedTable}</span> table is currently empty.
                          </p>
                          <div className="text-sm text-gray-400">
                            <p>Table Structure: {tableData.columns.length} columns</p>
                            <p className="mt-1">
                              Columns: {tableData.columns.slice(0, 3).join(', ')}
                              {tableData.columns.length > 3 && ` (+${tableData.columns.length - 3} more)`}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  ) : null}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-gray-400 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 7v10c0 2.21 1.79 4 4 4h8c2.21 0 4-1.79 4-4V7M4 7c0-2.21 1.79-4 4-4h8c2.21 0 4 1.79 4-4V7M4 7h16" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Table</h3>
                  <p className="text-gray-500">Choose a table from the sidebar to view its data</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseViewer;
